import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Icon,
  Button,
  Label,
  TextField,
  Tooltip,
  Menu,
  MenuItem,
  Badge,
} from 'mw-style-react';
import useIntl from 'useIntl';
import cn from 'classnames';
import mes from './intl';
import '../../ScriptEditor.scss';

function StructureItem(props) {
  const {
    id,
    objType,
    title,
    icon,
    isSystem,
    isStatic,
    isNew,
    readOnly,
    level,
    activeItem,
    children,
    customActions,
    handleSelect,
    handleUpdate,
    handleRemove,
    handleDuplicate,
    handleShowPage,
  } = props;
  const t = useIntl();
  const [objTitle, setObjTitle] = useState(title);
  const [open, setOpen] = useState(isSystem || isNew);
  const [editMode, setEditMode] = useState(false);
  const [menu, toggleMenu] = useState(false);
  const isFolder = objType === 'folder';
  const isRootObj = level === 0;
  const defaultIcon = objType === 'folder' ? 'files' : 'file';
  const isActive =
    activeItem && activeItem.id === id && activeItem.objType === objType;
  const isPageFile =
    !isFolder && level === 2 && (title === 'config' || title === 'locale');

  const handleClickMenu = ({ value }) => {
    switch (value) {
      case 'remove':
        handleRemove();
        break;
      case 'rename':
        setEditMode(true);
        break;
      case 'copy':
        handleDuplicate();
        break;
      case 'static':
        handleUpdate(objTitle, !isStatic);
        break;
      default:
    }
    toggleMenu(false);
  };

  const renderMenu = () => (
    <div
      styleName="se__content__structure__item__menu"
      onClick={(e) => e.stopPropagation()}
    >
      <Menu onClick={handleClickMenu} onClose={() => toggleMenu(false)}>
        <MenuItem label={t(mes.duplicate)} value="copy" />
        <MenuItem
          label={isStatic ? t(mes.makeDynamicPage) : t(mes.makeStaticPage)}
          visibility={objType === 'folder' ? 'visible' : 'hidden'}
          value="static"
        />
        <MenuItem
          visibility={isSystem || readOnly ? 'hidden' : 'visible'}
          label={t(mes.rename)}
          value="rename"
        />
        <MenuItem
          visibility={isSystem || readOnly ? 'disabled' : 'visible'}
          label={t(mes.remove)}
          value="remove"
        />
      </Menu>
    </div>
  );

  /**
   * Rename file/folder
   */
  const handleRename = () => {
    handleUpdate(objTitle);
    setEditMode(false);
  };

  const paddingLevel = level > 4 ? 4 : level;
  let padding = 12 + paddingLevel * 16;
  if (!isFolder && isRootObj) {
    padding -= 4;
  } else if (!isFolder || !children) {
    padding += 16;
  }

  return (
    <>
      <div
        key={id}
        styleName={cn('se__content__structure__item', {
          active: isActive,
          root: isRootObj,
        })}
        style={{ paddingLeft: padding }}
        onClick={handleSelect}
      >
        <div
          styleName="se__content__structure__item__title"
          onClick={() => {
            if (isFolder && children) {
              setOpen(!open);
              if (!open) handleShowPage();
            }
          }}
          onDoubleClick={(e) => {
            e.stopPropagation();
            if (isSystem || readOnly) return;
            setEditMode(true);
          }}
        >
          {isFolder && children ? (
            <Icon
              styleName={cn('se__content__structure__item__dropdown', {
                close: !open,
              })}
              type="dropdown"
              size="micro"
              onClick={() => setOpen(!open)}
            />
          ) : null}
          <Icon
            type={icon || defaultIcon}
            color={!icon && isFolder ? '#FEC603' : null}
          />
          {isStatic ? (
            <Badge
              styleName="se__content__structure__item__badge"
              value="S"
              size="medium"
            />
          ) : null}
          {editMode ? (
            <TextField
              styleName="se__content__structure__item__title__edit"
              value={objTitle}
              length={255}
              size="small"
              bordered={true}
              autoSelect="mount"
              autoFocus={true}
              onBlur={handleRename}
              onKeyUp={(e) => {
                if (e.key !== 'Enter') return;
                handleRename();
              }}
              onChange={({ value }) => setObjTitle(value)}
            />
          ) : (
            <Tooltip
              value={`${title}${isStatic ? `. ${t(mes.staticPage)}` : ''}`}
            >
              <Label value={title} />
            </Tooltip>
          )}
        </div>
        {!editMode && !readOnly ? (
          <div styleName="se__content__structure__item__actions">
            {customActions}
            <Button
              styleName="se__content__structure__item__actions__btn"
              type="tertiary"
              size="small"
              icon="more"
              fontWeight="normal"
              onClick={(e) => {
                e.stopPropagation();
                toggleMenu(!menu);
              }}
              visibility={isPageFile ? 'hidden' : 'visible'}
            />
          </div>
        ) : null}
        {menu ? renderMenu() : null}
      </div>
      {open ? children : null}
    </>
  );
}

StructureItem.propTypes = {
  level: PropTypes.number,
  id: PropTypes.number,
  title: PropTypes.string,
  icon: PropTypes.string,
  objType: PropTypes.string,
  activeItem: PropTypes.object,
  isSystem: PropTypes.bool,
  isStatic: PropTypes.bool,
  isNew: PropTypes.bool,
  readOnly: PropTypes.bool,
  children: PropTypes.node,
  customActions: PropTypes.node,
  handleSelect: PropTypes.func,
  handleUpdate: PropTypes.func,
  handleRemove: PropTypes.func,
  handleDuplicate: PropTypes.func,
  handleShowPage: PropTypes.func,
};

export default StructureItem;
